import { useQuery } from "@tanstack/react-query";
import { formatUsdCompact, type Search } from "@po/shared";
import { api, qs, type Kpis } from "../lib/api";
import { int, MONEY_CAPTION } from "../lib/format";
import { useDashboard } from "../pages/Dashboard";

const ACTIVE = ["draft", "awaiting_superintendent", "sourcing", "ready_to_order", "po_in_approval", "ordered", "partly_received"] as const;

/** Four figures in one strip, the same on every Procurement tab; a click applies the figure's filter and jumps (SPEC §7). */
export function KpiStrip() {
  const { filters, setSearch } = useDashboard();
  const { data: k, isFetching } = useQuery({ queryKey: ["kpis", filters], queryFn: () => api<Kpis>(`/kpis${qs(filters)}`) });
  if (!k) return <div className="card h-[70px] animate-pulse" />;
  const cells: { title: string; value: string; sub: string; go: Partial<Search> }[] = [
    { title: "Active req.", value: int(k.active), sub: "draft to partly received", go: { tab: "requisitions", stage: [...ACTIVE] } },
    {
      title: "Awaiting approval",
      value: int(k.awaiting.req + k.awaiting.po),
      sub: `${int(k.awaiting.req)} req · ${int(k.awaiting.po)} PO`,
      go: { tab: "requisitions", stage: ["awaiting_superintendent"] },
    },
    {
      title: "Median cycle",
      value: k.cycle.median == null ? "—" : `${Math.round(k.cycle.median)} d`,
      sub: k.cycle.target != null ? `target ${k.cycle.target} d` : k.cycle.pctWithinTarget != null ? `${k.cycle.pctWithinTarget} % within target` : "",
      go: { tab: "overview" },
    },
    { title: "PO pending receipt", value: formatUsdCompact(k.openCommitments), sub: MONEY_CAPTION, go: { tab: "spend" } },
  ];
  return (
    <section aria-label="Key figures" className={`card grid grid-cols-2 md:grid-cols-4 md:divide-x divide-line ${isFetching ? "opacity-80" : ""}`}>
      {cells.map((c) => (
        <button key={c.title} onClick={() => setSearch(c.go)} className="text-left px-4 py-2.5 hover:bg-hover transition-colors">
          <div className="text-[12px] font-medium text-ink2">{c.title}</div>
          <div className="text-[20px] font-bold leading-tight tnum text-ink">{c.value}</div>
          <div className="text-[11px] text-muted tnum">{c.sub}</div>
        </button>
      ))}
    </section>
  );
}
